import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Input } from "@progress/kendo-react-inputs";
import Button from "../buttons/Button";
import { useValidation } from "../utils/ValidationProvider";
import "./Input.scss";

/**
 * Input component
 * @module
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {string} name - Name
 * @param {bool} [required] - Is input required?
 * @param {func} onChange - Function invoked on value change
 * @param {(string | number)} [value] - Current value
 * @param {bool} [disabled] - Is input readonly?
 * @param {string} [type] - Type of input
 * @param {string} [placeholder] - Input placeholder
 * @param {func} [onClick] - Function invoked on click, if input has button
 * @param {bool} [autofocus] - Should input be focused?
 * @param {number} [minLength] - Minimum length
 * @param {number} [maxLength] - Maximum length
 * @param {string} [pattern] - Regex for validity check
 * @param {bool} [readOnly] - Read only
 * @param {number} [tabIndex] - Input tabindex
 * @param {bool} [isPicker] - Is picker
 * @param {func} [onBlur] - Function invoked on input blur
 * @param {string} [autoComplete] - HTML attribute: autocomplete
 */

const InputComponent = ({
  validationMessage,
  validationAllowed,
  name,
  required,
  recommended,
  onChange,
  value,
  disabled,
  readOnly,
  type,
  placeholder,
  onClick,
  autofocus,
  minLength,
  maxLength,
  min,
  max,
  pattern,
  tabIndex,
  onBlur,
  valid,
  accept,
  copyable,
  title,
  step,
  multiple,
  autoComplete,
  isInvalid
}) => {
  const [copied, setCopied] = useState(false);
  const [local, setLocal] = useState(value ?? "");
  const [focused, setFocused] = useState(false);
  const field = useMemo(() => ({
    field: name,
    title: title,
    type: type,
    min: min,
    max: max,
    minLength: minLength,
    maxLength: maxLength,
    validationMessage: validationMessage,
    pattern: pattern,
    required: required,
    disabled: disabled,
    recommended: recommended,
    validationAllowed: validationAllowed,
    isInvalid: isInvalid
  }), [disabled, max, maxLength, min, minLength, name, pattern, required, title, type, validationMessage, recommended, validationAllowed, isInvalid]);

  const { validate, setValidationValue } = useValidation(field, value);

  useEffect(() => {
    if (!focused && value !== local) setLocal(value ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused]);

  const handleBlur = (e) => {
    setFocused(false);
    validate();
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleFocus = () => {
    setFocused(true);
    setLocal(value ?? "");
  };

  const handleCopy = () => {
    if (!value) return;

    navigator.clipboard.writeText(value);
    setCopied(true)
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenUrl = () => {
    window.open(value);
  };

  const handleChange = (e) => {
    const inputValue = type && type === "file" ? e.target._input : e.target.value.replace(/[\u200B-\u200D\uFEFF]/g, '');
    setLocal(inputValue);
    setValidationValue(inputValue)
    onChange(name, inputValue);
  };

  return (
    <div className="input-wraper">
      <Input
        data-testid={name || title}
        className={`form-input input ${disabled ? "disabled-field" : required ? "required-field" : (recommended ? "recommended-field" : "default-field")}`}
        validationMessage={validationMessage || "Povinné"}
        required={required}
        onChange={handleChange}
        value={focused ? local : (value ?? "")}
        // disabled={disabled && !copyable}
        type={type}
        placeholder={placeholder}
        onClick={onClick}
        autoFocus={autofocus}
        minLength={minLength}
        maxLength={maxLength}
        min={min}
        max={max}
        pattern={pattern}
        tabIndex={tabIndex}
        onBlur={handleBlur}
        onFocus={handleFocus}
        readOnly={disabled || readOnly}
        valid={valid}
        accept={accept}
        title={title}
        step={step}
        multiple={multiple}
        autoComplete={autoComplete || "new-password"}
      />
      <div className="actions">
        {copyable &&
          <div className="copyable">
            <Button
              className="copyable-button"
              primary={true}
              icon={copied ? 'check' : 'copy'}
              onClick={handleCopy}
              disabled={!value}
              title={copied ? "Text skopírovaný" : "Kopírovať text"}
            />
          </div>
        }
        {type === "url" && value && (
          <div className="hyperlink">
            <Button
              className="hyperlink-button"
              icon="hyperlink-open-sm"
              onClick={handleOpenUrl}
              disabled={false}
              title="Otvoriť url"
            />
          </div>
        )}
      </div>
    </div>
  );
};

InputComponent.propTypes = {
  validationMessage: PropTypes.string,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  onClick: PropTypes.func,
  autofocus: PropTypes.bool,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  pattern: PropTypes.string,
  tabIndex: PropTypes.number,
  isPicker: PropTypes.bool,
  onBlur: PropTypes.func,
  valid: PropTypes.bool,
  accept: PropTypes.string,
  title: PropTypes.string,
  step: PropTypes.string,
  multiple: PropTypes.bool,
  autoComplete: PropTypes.string
};

export default InputComponent;